/*eslint max-lines-per-function: ["error", 300]*/
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { EstimateItemPurchaseDeficienciesSearch } from '../../components/EstimateItemPurchaseDeficiencies/EstimateItemPurchaseDeficienciesSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useNavigation } from '../../../common/navigation';
import { MailTeamModal } from '../../components/MailTeam/MailTeamModal';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import { useAuth } from '../../../common/hooks/useAuth';
import Button from '../../../common/components/Button/Button';
// import { useNotifications } from '../../../common/hooks/useNotifications';
import { CardTitleWithInfo } from '../../../app/components/CardTitleWithInfo';
import { EstimateItemPurchaseDeficienciesInfo } from '../../../translations/instructions/EstimateItemPurchaseDeficienciesInfo';

enum EstimateItemTabs {
  WITHOUT_TV = 'withoutTv',
  WITH_TV = 'Tv',
}

export const EstimateItemPurchaseDeficienciesView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const auth = useAuth();
  const { t, tk } = useTranslation('projectLng');
  const [show, setShow] = useState(false);
  const [tv, setTv] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selectedNoTvIds, setSelectedNoTvIds] = useState<string[]>([]);
  const [buttonNoTVTitle, setButtonNoTVTitle] = useState<string>(
    t(tk.attachment.sendRemindersWithoutTv),
  );
  const [selectedTvIds, setSelectedTvIds] = useState<string[]>([]);
  const [buttonTVTitle, setButtonTVTitle] = useState<string>(
    t(tk.attachment.sendRemindersTv),
  );

  useEffect(() => {
    setSelectedTvIds([]);
    setSelectedNoTvIds([]);
    setButtonTVTitle(`${t(tk.attachment.sendRemindersTv)}`);
    setButtonNoTVTitle(`${t(tk.attachment.sendRemindersWithoutTv)}`);
  }, [pok, t, tk]);

  const getDataWithoutTV = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.estimateItems.getPurchaseDeficiencies(
          t,
          tk,
          refresh,
          setRefresh,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          false,
        )) as GridGetterFunction,
    [pok.estimateItems, refresh, setRefresh, t, tk],
  );

  const getCountWithoutTV = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.estimateItems.getPurchaseDeficienciesCount(
          filterText,
          filterColumns,
          false,
        )) as GridCountFunction,
    [pok.estimateItems],
  );
  const getDataWithTV = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.estimateItems.getPurchaseDeficiencies(
          t,
          tk,
          refresh,
          setRefresh,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          true,
        )) as GridGetterFunction,
    [pok.estimateItems, refresh, setRefresh, t, tk],
  );

  const getCountWithTV = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.estimateItems.getPurchaseDeficienciesCount(
          filterText,
          filterColumns,
          true,
        )) as GridCountFunction,
    [pok.estimateItems],
  );

  const handleClick = async (id: string) => {
    const estimateItem = await pok.estimateItems.getById(id);
    return nav.projectDetails(
      estimateItem.estimateByMonth.project.id,
      true,
      'estimates',
    );
  };

  const handleClickNoTvForFinance = (key: string) => {
    let si: string[];

    if (!selectedNoTvIds.includes(key)) {
      si = [...selectedNoTvIds, key];
    } else {
      si = selectedNoTvIds.filter(o => o !== key);
    }

    setButtonNoTVTitle(
      si.length > 0
        ? `${t(tk.attachment.sendRemindersWithoutTv)} (${t(tk.common.itemsSelected, { number: si.length })})`
        : `${t(tk.attachment.sendRemindersWithoutTv)}`,
    );
    setSelectedNoTvIds(si);
  };

  const handleClickTvForFinance = (key: string) => {
    let si: string[];

    if (!selectedTvIds.includes(key)) {
      si = [...selectedTvIds, key];
    } else {
      si = selectedTvIds.filter(o => o !== key);
    }

    setButtonTVTitle(
      si.length > 0
        ? `${t(tk.attachment.sendRemindersTv)} (${t(tk.common.itemsSelected, { number: si.length })})`
        : `${t(tk.attachment.sendRemindersTv)}`,
    );
    setSelectedTvIds(si);
  };

  const handleMailWithoutTv = () => {
    setTv(false);
    setShow(true);
  };

  const handleMailWithTv = () => {
    setTv(true);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleTabChange = () => {
    setSelectedTvIds([]);
    setSelectedNoTvIds([]);
    setButtonTVTitle(`${t(tk.attachment.sendRemindersTv)}`);
    setButtonNoTVTitle(`${t(tk.attachment.sendRemindersWithoutTv)}`);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <CardTitleWithInfo
          title={`${t(tk.attachment.FVZLacks)} ${t(tk.prLang.genitiveProject)}`}
        >
          <EstimateItemPurchaseDeficienciesInfo />
        </CardTitleWithInfo>

        <TabsHistory
          activeKey={EstimateItemTabs.WITHOUT_TV}
          id="product"
          className="mb-3"
          mountOnEnter
          unmountOnExit
          handleTabChange={handleTabChange}
        >
          <Tab
            eventKey={EstimateItemTabs.WITHOUT_TV}
            title={t(tk.attachment.withoutTv)}
            className="m-3"
          >
            {auth.check(PermissionsEnum.Finance) && (
              <Button
                className="mb-4"
                variant="outline-primary"
                onClick={handleMailWithoutTv}
              >
                {buttonNoTVTitle}
              </Button>
            )}
            <EstimateItemPurchaseDeficienciesSearch
              getData={getDataWithoutTV}
              getCount={getCountWithoutTV}
              onRowClick={
                auth.check(PermissionsEnum.Finance)
                  ? handleClickNoTvForFinance
                  : handleClick
              }
              refresh={refresh}
              finance={auth.check(PermissionsEnum.Finance)}
              enableMultiSelect={auth.check(PermissionsEnum.Finance)}
              selectedKeys={selectedNoTvIds}
            />
          </Tab>
          <Tab
            eventKey={EstimateItemTabs.WITH_TV}
            title={t(tk.attachment.withTv)}
            className="m-3"
          >
            {auth.check(PermissionsEnum.Finance) && (
              <Button
                className="mb-4"
                variant="outline-primary"
                onClick={handleMailWithTv}
              >
                {buttonTVTitle}
              </Button>
            )}
            <EstimateItemPurchaseDeficienciesSearch
              getData={getDataWithTV}
              getCount={getCountWithTV}
              onRowClick={
                auth.check(PermissionsEnum.Finance)
                  ? handleClickTvForFinance
                  : handleClick
              }
              refresh={refresh}
              finance={auth.check(PermissionsEnum.Finance)}
              enableMultiSelect={auth.check(PermissionsEnum.Finance)}
              selectedKeys={selectedTvIds}
            />
          </Tab>
        </TabsHistory>
        <MailTeamModal
          show={show}
          handleClose={handleClose}
          tv={tv}
          sendFunction={pok.emails.sendPurchaseDeficienciesEmail}
          selectedIds={tv ? selectedTvIds : selectedNoTvIds}
          isPurchase={true}
        />
      </Card.Body>
    </Card>
  );
};
